// components/items/SaleItem.tsx
import React, { useState } from "react";
import {
  Eye,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  User,
  RotateCcw,
  X,
  Check,
  Ban,
  DollarSign,
  TrendingUp,
} from "lucide-react";
import { Sale, SaleStatus } from "../../../../types/features/administration/BuyAndSell";

interface SaleItemProps {
  sale: Sale;
  onAction: (action: string, sale: Sale, data?: any) => void;
  vendeur?: any;
}

const SaleItem: React.FC<SaleItemProps> = ({ sale, onAction, vendeur }) => {
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const [loadingChangeStatus, setLoadingChangeStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<SaleStatus>(SaleStatus.COMPLETED);
  const [note, setNote] = useState("");

  const getStatusBadge = (status: SaleStatus) => {
    const statusConfig = {
      [SaleStatus.COMPLETED]: {
        bg: "bg-emerald-100",
        text: "text-emerald-700",
        border: "border-emerald-300",
        label: "Complété",
        icon: CheckCircle,
      },

      [SaleStatus.PENDING]: {
        bg: "bg-blue-100",
        text: "text-blue-700",
        border: "border-blue-300",
        label: "En Attente",
        icon: Clock,
      },
      [SaleStatus.CANCELLED]: {
        bg: "bg-gray-100",
        text: "text-gray-700",
        border: "border-gray-300",
        label: "Annulé",
        icon: Ban,
      },
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${config.bg} ${config.text} border ${config.border}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    return `${amount.toLocaleString()} ${currency}`;
  };

  const canChangeStatus = sale.status !== SaleStatus.COMPLETED && sale.status !== SaleStatus.CANCELLED;
  const isCanceled = sale.status === SaleStatus.CANCELLED;

  const handleChangeStatus = async () => {
    setLoadingChangeStatus(true);
    try {
      await onAction("change-status", sale, { status: selectedStatus, note });
      setShowChangeStatusModal(false);
      setNote("");
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error);
    } finally {
      setLoadingChangeStatus(false);
    }
  };

  const handleCancelSale = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler cette vente ?")) return;
    try {
      await onAction("change-status", sale, { status: SaleStatus.CANCELLED, note: "Vente annulée par l'utilisateur" });
    } catch (error) {
      console.error("Erreur lors de l'annulation:", error);
    }
  };

  return (
    <>
      <div className="mb-3 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
        <div className="p-4">
          <div className="grid grid-cols-12 gap-4 items-center">
            {/* ID & Date */}
            <div className="col-span-2">
              <p className="font-mono font-semibold text-gray-900">#{sale.id?.slice(-8) || "N/A"}</p>
              <p className="mt-1 text-xs text-gray-500">{formatDate(sale.created_at)}</p>
            </div>

            {/* Vendeur */}
            <div className="col-span-2">
              <div className="flex gap-2 items-center">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-400 truncate">Vendu par:</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {vendeur?.last_name || ""} {vendeur?.first_name || ""}
                  </p>
                  <p className="text-xs text-gray-500">{vendeur?.email}</p>
                </div>
              </div>
            </div>

            {/* Montants Vente */}
            <div className="col-span-2">
              <p className="text-lg font-bold text-gray-900">{formatAmount(sale.somme_a_vendre, sale.monaie_vente)}</p>
              <p className="mt-1 text-xs text-gray-500">Taux vendeur: {sale.taux_vendeur}</p>
            </div>

            {/* Montants Achat */}
            <div className="col-span-2">
              <p className="text-lg font-bold text-emerald-600">
                {formatAmount(sale.somme_a_recevoir, sale.monaie_achat)}
              </p>
              <p className="mt-1 text-xs text-gray-500">Taux marchand: {sale.taux_marchand}</p>
            </div>

            {/* Marge Marchand */}
            <div className="col-span-1">
              <div className="flex flex-col items-center p-2 bg-purple-50 rounded-lg border border-purple-200">
                <TrendingUp className="w-4 h-4 text-purple-600" />
                <p className="text-sm font-bold text-purple-700">
                  {formatAmount(sale.somme_marchand, sale.monaie_achat)}
                </p>
                <p className="text-xs text-gray-500">Marge</p>
              </div>
            </div>

            {/* Statut */}
            <div className="col-span-1">{getStatusBadge(sale.status)}</div>

            {/* Actions */}
            <div className="flex flex-col col-span-1 gap-1">
              <button
                onClick={() => onAction("view", sale)}
                className="p-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors border border-blue-200 flex items-center justify-center"
                title="Voir détails"
              >
                <Eye className="w-3.5 h-3.5" />
              </button>

              {canChangeStatus && (
                <button
                  onClick={() => setShowChangeStatusModal(true)}
                  className="p-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded transition-colors border border-green-200 flex items-center justify-center"
                  title="Changer statut"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
              )}

              {!isCanceled && sale.status !== SaleStatus.COMPLETED && (
                <button
                  onClick={handleCancelSale}
                  className="p-1.5 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-200 rounded transition-colors border border-gray-300 flex items-center justify-center"
                  title="Annuler la vente"
                >
                  <Ban className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Changement de Statut */}
      {showChangeStatusModal && (
        <div className="flex overflow-y-auto fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
          <div className="my-8 w-full max-w-md bg-white rounded-2xl shadow-2xl">
            <div className="flex flex-shrink-0 justify-between items-center px-6 py-5 bg-gradient-to-r from-green-600 to-green-700 rounded-t-2xl">
              <h2 className="text-xl font-bold text-white">Changer le statut de la vente</h2>
              <button
                onClick={() => {
                  setShowChangeStatusModal(false);
                  setNote("");
                }}
                className="flex-shrink-0 p-1 text-white rounded-lg transition-colors hover:bg-green-800"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
              <div className="p-6 space-y-6">
                <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200">
                  <p className="mb-2 text-xs font-semibold text-gray-600">Vente ID</p>
                  <p className="font-mono text-lg font-black text-gray-900">#{sale.id?.slice(-8) || "N/A"}</p>
                </div>

                <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200">
                  <p className="mb-2 text-xs font-semibold text-blue-600">Montant Vente</p>
                  <p className="text-2xl font-black text-blue-900">
                    {formatAmount(sale.somme_a_vendre, sale.monaie_vente)}
                  </p>
                  <p className="mt-1 text-sm text-blue-700">
                    → {formatAmount(sale.somme_a_recevoir, sale.monaie_achat)}
                  </p>
                </div>

                <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border-2 border-purple-200">
                  <p className="mb-3 text-xs font-semibold text-purple-600">Nouveau Statut</p>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value as SaleStatus)}
                    className="px-4 py-3 w-full text-sm font-semibold text-gray-900 bg-white rounded-lg border-2 border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={SaleStatus.COMPLETED}>COMPLETED - Complété</option>
                    <option value={SaleStatus.PENDING}>PENDING - En attente</option>
                    <option value={SaleStatus.CANCELLED}>CANCELLED - Annulé</option>
                  </select>
                </div>

                <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl border-2 border-indigo-200">
                  <label className="block mb-3 text-xs font-semibold text-indigo-600">Note (optionnelle)</label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Ajoutez une note concernant ce changement de statut..."
                    rows={4}
                    className="px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border-2 border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  />
                  <p className="mt-2 text-xs text-indigo-600">{note.length}/500 caractères</p>
                </div>

                <div className="p-4 bg-yellow-50 rounded-xl border-2 border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    <span className="font-bold">⚠️ Attention:</span> Cette action va modifier le statut de la vente en{" "}
                    <span className="font-bold">{selectedStatus}</span>.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-shrink-0 gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl border-t-2 border-gray-200">
              <button
                onClick={() => {
                  setShowChangeStatusModal(false);
                  setNote("");
                }}
                disabled={loadingChangeStatus}
                className="flex-1 px-4 py-3 font-bold text-gray-700 rounded-lg border-2 border-gray-300 transition-colors hover:bg-gray-100 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleChangeStatus}
                disabled={loadingChangeStatus}
                className="flex flex-1 gap-2 justify-center items-center px-4 py-3 font-bold text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:bg-gray-400"
              >
                {loadingChangeStatus ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Traitement...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Confirmer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SaleItem;
